// src/features/experiential/toolRegistry.tsx
// The TechniqueKey → tool-component registry, shared by ExperientialPanel
// (Session panel dialog) and the Toolkit's ExperientialWorkspace.
import React from "react";
import { TechniqueKey } from "./types";
import { SCHEMA_MODES } from "./lib/schemaModes";
import {
  IFS_PARTS, BUS_PASSENGERS, EMPTY_CHAIR_ROLES,
  TAPE_FORWARD_ROLES, COUPLES_CYCLE_ROLES, BRAIN_CAST_ROLES, PLAYBACK_ROLES, TALE_ROLES,
} from "./lib/roleSets";
import { EnsembleVoiceTool } from "./parts/EnsembleVoiceTool";
import { SingleVoiceTool } from "./parts/SingleVoiceTool";
import { SingleImageTool } from "./parts/SingleImageTool";
import { FeltSenseTool } from "./parts/FeltSenseTool";
import { MetaphorDeckTool } from "./parts/MetaphorDeckTool";
import { DistantScreenTool } from "./parts/DistantScreenTool";
import { ImageVoiceTool } from "./parts/ImageVoiceTool";
import { NightmareRescriptTool } from "./parts/NightmareRescriptTool";
import { PendulationTool } from "./parts/PendulationTool";
import { DefusionVoiceTool } from "./parts/DefusionVoiceTool";
import { ImageryRescriptTool } from "./parts/ImageryRescriptTool";

export interface ToolProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
}

export interface TechniqueDef {
  key: TechniqueKey;
  group: "voice" | "image" | "combined";
  render: (p: ToolProps) => React.ReactNode;
}

export const TECHNIQUES: TechniqueDef[] = [
  {
    key: "schema_chair", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={SCHEMA_MODES}
        roleI18nPrefix="experiential.modes"
        roleLabelKey="experiential.mode"
        playback="together"
        addLabelKey="experiential.addChair"
        suggestFromSessions
      />
    ),
  },
  {
    key: "spatial_modes", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={SCHEMA_MODES}
        roleI18nPrefix="experiential.modes"
        roleLabelKey="experiential.mode"
        playback="together"
        addLabelKey="experiential.addChair"
        spatial
        suggestFromSessions
      />
    ),
  },
  {
    key: "empty_chair", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={EMPTY_CHAIR_ROLES}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.chairLabel"
        playback="sequence"
        addLabelKey="experiential.addTurn"
      />
    ),
  },
  {
    key: "parts_dialogue", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={IFS_PARTS}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.part"
        playback="sequence"
        addLabelKey="experiential.addTurn"
      />
    ),
  },
  {
    key: "bus_passengers", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={BUS_PASSENGERS}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.passenger"
        playback="together"
        addLabelKey="experiential.addPassenger"
      />
    ),
  },
  {
    key: "compassionate_letter", group: "voice",
    render: (p) => (
      <SingleVoiceTool
        {...p}
        technique="compassionate_letter"
        textLabelKey="experiential.letterText"
        defaultVoice="Grace"
      />
    ),
  },
  {
    key: "needed_words", group: "voice",
    render: (p) => (
      <SingleVoiceTool
        {...p}
        technique="needed_words"
        textLabelKey="experiential.neededText"
        defaultVoice="Arthur"
      />
    ),
  },
  {
    key: "defusion_voice", group: "voice",
    render: (p) => <DefusionVoiceTool {...p} />,
  },
  {
    key: "loop_tape", group: "voice",
    render: (p) => (
      <SingleVoiceTool {...p} technique="loop_tape" textLabelKey="experiential.loopTapeText" defaultVoice="Mason" />
    ),
  },
  {
    key: "tape_forward", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={TAPE_FORWARD_ROLES}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.take"
        playback="sequence"
        addLabelKey="experiential.addTurn"
      />
    ),
  },
  {
    key: "urge_surfing", group: "voice",
    render: (p) => (
      <SingleVoiceTool {...p} technique="urge_surfing" textLabelKey="experiential.narrationText" defaultVoice="Grace" />
    ),
  },
  {
    key: "couples_cycle", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={COUPLES_CYCLE_ROLES}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.partner"
        playback="sequence"
        addLabelKey="experiential.addTurn"
      />
    ),
  },
  {
    key: "brain_cast", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={BRAIN_CAST_ROLES}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.character"
        playback="sequence"
        addLabelKey="experiential.addTurn"
      />
    ),
  },
  {
    key: "playback_theatre", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={PLAYBACK_ROLES}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.character"
        playback="sequence"
        addLabelKey="experiential.addTurn"
      />
    ),
  },
  {
    key: "tale_story", group: "voice",
    render: (p) => (
      <EnsembleVoiceTool
        {...p}
        roles={TALE_ROLES}
        roleI18nPrefix="experiential.roles"
        roleLabelKey="experiential.character"
        playback="sequence"
        addLabelKey="experiential.addChapter"
      />
    ),
  },
  {
    key: "metaphor_induction", group: "voice",
    render: (p) => (
      <SingleVoiceTool {...p} technique="metaphor_induction" textLabelKey="experiential.inductionText" defaultVoice="Arthur" />
    ),
  },
  {
    key: "sleep_story", group: "voice",
    render: (p) => (
      <SingleVoiceTool {...p} technique="sleep_story" textLabelKey="experiential.sleepStoryText" defaultVoice="Elise" />
    ),
  },
  {
    key: "imagery_rescript", group: "image",
    render: (p) => <ImageryRescriptTool patientId={p.patientId} sessionId={p.sessionId} getToken={p.getToken} />,
  },
  {
    key: "felt_sense", group: "image",
    render: (p) => <FeltSenseTool patientId={p.patientId} sessionId={p.sessionId} getToken={p.getToken} />,
  },
  {
    key: "safe_container", group: "image",
    render: (p) => (
      <SingleImageTool
        patientId={p.patientId}
        sessionId={p.sessionId}
        getToken={p.getToken}
        promptLabelKey="experiential.containerDesc"
      />
    ),
  },
  {
    key: "safe_place", group: "image",
    render: (p) => (
      <SingleImageTool
        patientId={p.patientId}
        sessionId={p.sessionId}
        getToken={p.getToken}
        promptLabelKey="experiential.safePlaceDesc"
      />
    ),
  },
  {
    key: "metaphor_deck", group: "image",
    render: (p) => <MetaphorDeckTool patientId={p.patientId} sessionId={p.sessionId} getToken={p.getToken} />,
  },
  {
    key: "distant_screen", group: "image",
    render: (p) => <DistantScreenTool patientId={p.patientId} sessionId={p.sessionId} getToken={p.getToken} />,
  },
  {
    key: "miracle_question", group: "combined",
    render: (p) => (
      <ImageVoiceTool
        {...p}
        technique="miracle_question"
        visualKind="single"
        imageLabelKey="experiential.miracleDesc"
        wordsLabelKey="experiential.miracleWords"
        defaultVoice="Sofia"
      />
    ),
  },
  {
    key: "compassionate_other", group: "combined",
    render: (p) => (
      <ImageVoiceTool
        {...p}
        technique="compassionate_other"
        visualKind="single"
        imageLabelKey="experiential.otherDesc"
        wordsLabelKey="experiential.otherWords"
        defaultVoice="Grace"
      />
    ),
  },
  {
    key: "worry_monster", group: "combined",
    render: (p) => (
      <ImageVoiceTool
        {...p}
        technique="worry_monster"
        visualKind="creature"
        imageLabelKey="experiential.monsterDesc"
        wordsLabelKey="experiential.monsterWords"
        defaultVoice="Owen"
      />
    ),
  },
  {
    key: "brave_rehearsal", group: "combined",
    render: (p) => (
      <ImageVoiceTool
        {...p}
        technique="brave_rehearsal"
        visualKind="single"
        imageLabelKey="experiential.braveDesc"
        wordsLabelKey="experiential.braveWords"
        defaultVoice="Grace"
      />
    ),
  },
  {
    key: "nightmare_rescript", group: "combined",
    render: (p) => <NightmareRescriptTool {...p} />,
  },
  {
    key: "pendulation", group: "combined",
    render: (p) => <PendulationTool {...p} />,
  },
];

export function findTechnique(key: TechniqueKey): TechniqueDef | undefined {
  return TECHNIQUES.find((tq) => tq.key === key);
}
